// =====================================================================
// amendmentSigning.js — NNHC Sprint 5b amendment workflow helper
// =====================================================================
// Wraps the 4 server-side RPCs (add_addendum, propose_amendment,
// approve_amendment, reject_amendment) with:
//
//   1. Password re-prompt (60-second re-auth window per CMS § 3.3.2.4)
//   2. Session token retrieval from your existing auth pattern
//   3. Telemetry collection (IP optional, user agent, device fingerprint)
//   4. Standardized error mapping (DB errors → user-facing messages)
//   5. Single return shape: { ok, signature_id, amendment_id, error }
//
// Drop this in: src/lib/amendmentSigning.js
// Then import in ClinicalPortal.jsx:
//   import {
//     signAddendum, proposeAmendment,
//     approveAmendment, rejectAmendment,
//     promptPasswordReauth
//   } from "../lib/amendmentSigning";
//
// Requires: @supabase/supabase-js client instance accessible via the
// supabase argument or imported from your existing client module.
// =====================================================================

// ---------------------------------------------------------------------
// Session token retrieval (Supabase Auth access token)
// ---------------------------------------------------------------------
export async function getSessionToken(supabase) {
    if (!supabase?.auth) return null;
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || null;
}

// ---------------------------------------------------------------------
// Password re-prompt (returns { ok, reauthVerifiedAt } or { ok: false })
// ---------------------------------------------------------------------
// Verifies the user's CURRENT password by calling
// supabase.auth.signInWithPassword against their authenticated email.
// On success we mint a client-side timestamp; the server-side RPCs
// re-validate it falls inside the 60-second re-auth window.
export async function promptPasswordReauth(supabase, email, opts = {}) {
    const { promptText, modalComponent } = opts;

    let password;
    if (modalComponent) {
        password = await modalComponent({
            title: "Re-enter password to sign",
            message: promptText || "For your security, please re-enter your password to sign this record.",
        });
    } else {
        password = window.prompt(
            promptText || "Re-enter your password to sign this record:"
        );
    }

    if (!password) {
        return { ok: false, error: "Password required" };
    }

    if (!email) {
        const { data: userData } = await supabase.auth.getUser();
        email = userData?.user?.email;
    }
    if (!email) {
        return { ok: false, error: "Not logged in" };
    }

    const { error } = await supabase.auth.signInWithPassword({
        email: String(email).toLowerCase().trim(),
        password,
    });

    if (error) {
        return { ok: false, error: "Incorrect password" };
    }

    return {
        ok: true,
        reauthVerifiedAt: new Date().toISOString(),
    };
}

// ---------------------------------------------------------------------
// Telemetry helpers (optional, all values may be null)
// ---------------------------------------------------------------------
function getUserAgent() {
    if (typeof navigator === "undefined") return null;
    return navigator.userAgent || null;
}

function getDeviceFingerprint() {
    // Lightweight fingerprint: screen size + timezone + platform.
    // Real fingerprinting is privacy-questionable; this is enough to
    // distinguish "same user, different browser" for audit purposes.
    if (typeof window === "undefined") return null;
    try {
        const parts = [
            window.screen?.width,
            window.screen?.height,
            Intl.DateTimeFormat().resolvedOptions().timeZone,
            navigator.platform,
        ].filter(Boolean);
        return parts.join("|") || null;
    } catch {
        return null;
    }
}

// ---------------------------------------------------------------------
// Error mapping: DB errors → user-friendly messages
// ---------------------------------------------------------------------
function mapDbError(error) {
    if (!error) return "Unknown error";
    const msg = error.message || String(error);

    // Match the exact RAISE EXCEPTION text from each RPC
    if (msg.includes("Invalid or expired session")) {
        return "Your session has expired. Please log in again.";
    }
    if (msg.includes("Re-authentication expired")) {
        return "Re-authentication timed out. Please re-enter your password and try again.";
    }
    if (msg.includes("not authorized to sign")) {
        return "Your role does not permit signing clinical documents.";
    }
    if (msg.includes("not authorized to propose amendments")) {
        return "Your role does not permit proposing amendments.";
    }
    if (msg.includes("Approval requires Director of Nursing")) {
        return "Only the Director of Nursing or Administrator / RN may approve amendments.";
    }
    if (msg.includes("Rejection requires Director of Nursing")) {
        return "Only the Director of Nursing or Administrator / RN may reject amendments.";
    }
    if (msg.includes("No active e-signature consent")) {
        return "You must complete the e-signature consent form before you can sign.";
    }
    if (msg.includes("not pending")) {
        return "Another reviewer has already acted on this amendment. Refresh and try again.";
    }
    if (msg.includes("Self-approval not permitted") ||
        msg.includes("Self-rejection of own proposal")) {
        return "You cannot review your own proposal. Another supervisor must approve or reject it.";
    }
    if (msg.includes("pending amendment already exists")) {
        return "A pending amendment already exists for this field. Wait for review or reject it first.";
    }
    if (msg.includes("voided document") || msg.includes("Cannot amend a voided")) {
        return "This document has been voided and cannot be amended.";
    }
    if (msg.includes("Direct UPDATE of clinical_docs")) {
        // This means a developer bypassed the RPC and hit the trigger.
        // Surface it as an error so it gets caught in dev/QA.
        return "Internal error: amendment write attempted outside the sanctioned RPC. Please report this.";
    }
    if (msg.includes("Document not found")) {
        return "Document not found — it may have been deleted or you may not have access.";
    }
    if (msg.includes("min 10 chars") || msg.includes("min 20 chars")) {
        return "Reason or notes too short — please provide more detail.";
    }
    // Fallback: surface raw message for debugging
    return msg;
}

// ---------------------------------------------------------------------
// signAddendum — adds an immediate-applied addendum with audit signature
// ---------------------------------------------------------------------
export async function signAddendum(supabase, params) {
    const {
        docId,
        content,
        reauthVerifiedAt,
        ip = null,
    } = params;

    if (!docId) return { ok: false, error: "docId is required" };
    if (!content || content.trim().length < 10) {
        return { ok: false, error: "Addendum content too short (min 10 characters)" };
    }
    if (!reauthVerifiedAt) {
        return { ok: false, error: "Re-authentication required" };
    }

    const sessionToken = await getSessionToken(supabase);
    if (!sessionToken) {
        return { ok: false, error: "Not logged in" };
    }

    const { data, error } = await supabase.rpc("add_addendum", {
        p_session_token: sessionToken,
        p_doc_id: docId,
        p_content: content,
        p_reauth_verified_at: reauthVerifiedAt,
        p_signed_ip: ip,
        p_signed_user_agent: getUserAgent(),
        p_device_fingerprint: getDeviceFingerprint(),
    });

    if (error) {
        return { ok: false, error: mapDbError(error) };
    }

    const result = Array.isArray(data) ? data[0] : data;
    return {
        ok: true,
        signature_id: result?.signature_id,
        addendum_id: result?.addendum_id,
    };
}

// ---------------------------------------------------------------------
// proposeAmendment — proposes a pending amendment, requires DON approval
// ---------------------------------------------------------------------
export async function proposeAmendment(supabase, params) {
    const {
        docId,
        fieldPath,
        oldValue,
        newValue,
        reason,
        reauthVerifiedAt,
        ip = null,
    } = params;

    if (!docId) return { ok: false, error: "docId is required" };
    if (!fieldPath) return { ok: false, error: "fieldPath is required" };
    if (!reason || reason.trim().length < 10) {
        return { ok: false, error: "Amendment reason too short (min 10 characters)" };
    }
    if (!reauthVerifiedAt) {
        return { ok: false, error: "Re-authentication required" };
    }

    const sessionToken = await getSessionToken(supabase);
    if (!sessionToken) {
        return { ok: false, error: "Not logged in" };
    }

    const { data, error } = await supabase.rpc("propose_amendment", {
        p_session_token: sessionToken,
        p_doc_id: docId,
        p_field_path: fieldPath,
        p_old_value: oldValue ?? null,
        p_new_value: newValue ?? null,
        p_reason: reason,
        p_reauth_verified_at: reauthVerifiedAt,
        p_signed_ip: ip,
        p_signed_user_agent: getUserAgent(),
        p_device_fingerprint: getDeviceFingerprint(),
    });

    if (error) {
        return { ok: false, error: mapDbError(error) };
    }

    const result = Array.isArray(data) ? data[0] : data;
    return {
        ok: true,
        signature_id: result?.signature_id,
        amendment_id: result?.amendment_id,
    };
}

// ---------------------------------------------------------------------
// approveAmendment — DON approves a pending amendment
// ---------------------------------------------------------------------
// Atomically: writes form_data[field_path] = new_value, marks amendment
// approved, captures approver signature. Self-approval blocked server-side.
export async function approveAmendment(supabase, params) {
    const {
        docId,
        amendmentId,
        reviewNotes,
        reauthVerifiedAt,
        ip = null,
    } = params;

    if (!docId) return { ok: false, error: "docId is required" };
    if (!amendmentId) return { ok: false, error: "amendmentId is required" };
    if (!reviewNotes || reviewNotes.trim().length < 10) {
        return { ok: false, error: "Review notes too short (min 10 characters)" };
    }
    if (!reauthVerifiedAt) {
        return { ok: false, error: "Re-authentication required" };
    }

    const sessionToken = await getSessionToken(supabase);
    if (!sessionToken) {
        return { ok: false, error: "Not logged in" };
    }

    const { data, error } = await supabase.rpc("approve_amendment", {
        p_session_token: sessionToken,
        p_doc_id: docId,
        p_amendment_id: amendmentId,
        p_review_notes: reviewNotes,
        p_reauth_verified_at: reauthVerifiedAt,
        p_signed_ip: ip,
        p_signed_user_agent: getUserAgent(),
        p_device_fingerprint: getDeviceFingerprint(),
    });

    if (error) {
        return { ok: false, error: mapDbError(error) };
    }

    return {
        ok: true,
        signature_id: data,  // approve_amendment returns uuid directly
    };
}

// ---------------------------------------------------------------------
// rejectAmendment — DON rejects a pending amendment
// ---------------------------------------------------------------------
// form_data is NOT changed on rejection. Original value retained.
export async function rejectAmendment(supabase, params) {
    const {
        docId,
        amendmentId,
        reviewNotes,
        reauthVerifiedAt,
        ip = null,
    } = params;

    if (!docId) return { ok: false, error: "docId is required" };
    if (!amendmentId) return { ok: false, error: "amendmentId is required" };
    if (!reviewNotes || reviewNotes.trim().length < 10) {
        return { ok: false, error: "Review notes too short (min 10 characters)" };
    }
    if (!reauthVerifiedAt) {
        return { ok: false, error: "Re-authentication required" };
    }

    const sessionToken = await getSessionToken(supabase);
    if (!sessionToken) {
        return { ok: false, error: "Not logged in" };
    }

    const { data, error } = await supabase.rpc("reject_amendment", {
        p_session_token: sessionToken,
        p_doc_id: docId,
        p_amendment_id: amendmentId,
        p_review_notes: reviewNotes,
        p_reauth_verified_at: reauthVerifiedAt,
        p_signed_ip: ip,
        p_signed_user_agent: getUserAgent(),
        p_device_fingerprint: getDeviceFingerprint(),
    });

    if (error) {
        return { ok: false, error: mapDbError(error) };
    }

    return {
        ok: true,
        signature_id: data,
    };
}

// ---------------------------------------------------------------------
// Convenience: full flow with password prompt baked in
// ---------------------------------------------------------------------
// Use these if your modal component already handles "ask for password
// at submit time" — they bundle the re-auth + RPC into one call.

export async function signAddendumWithReauth(supabase, user, params) {
    const reauth = await promptPasswordReauth(supabase, user.username, {
        promptText: "Re-enter your password to sign this addendum.",
        modalComponent: params.passwordModal,
    });
    if (!reauth.ok) return reauth;
    return signAddendum(supabase, {
        ...params,
        reauthVerifiedAt: reauth.reauthVerifiedAt,
    });
}

export async function proposeAmendmentWithReauth(supabase, user, params) {
    const reauth = await promptPasswordReauth(supabase, user.username, {
        promptText: "Re-enter your password to propose this amendment.",
        modalComponent: params.passwordModal,
    });
    if (!reauth.ok) return reauth;
    return proposeAmendment(supabase, {
        ...params,
        reauthVerifiedAt: reauth.reauthVerifiedAt,
    });
}

export async function approveAmendmentWithReauth(supabase, user, params) {
    const reauth = await promptPasswordReauth(supabase, user.username, {
        promptText: "Re-enter your password to approve this amendment.",
        modalComponent: params.passwordModal,
    });
    if (!reauth.ok) return reauth;
    return approveAmendment(supabase, {
        ...params,
        reauthVerifiedAt: reauth.reauthVerifiedAt,
    });
}

export async function rejectAmendmentWithReauth(supabase, user, params) {
    const reauth = await promptPasswordReauth(supabase, user.username, {
        promptText: "Re-enter your password to reject this amendment.",
        modalComponent: params.passwordModal,
    });
    if (!reauth.ok) return reauth;
    return rejectAmendment(supabase, {
        ...params,
        reauthVerifiedAt: reauth.reauthVerifiedAt,
    });
}

// ---------------------------------------------------------------------
// Role gate helpers — use these in JSX render conditions
// ---------------------------------------------------------------------
// Mirror of the SQL helper functions fn_user_is_supervisor and
// fn_user_can_sign_clinical, so React-side gating matches what the DB
// will allow. UI-only — server still enforces the real check.

export function userIsSupervisor(user) {
    if (!user) return false;
    return (
        user.role === "Director of Nursing" ||
        user.role === "Administrator / RN" ||
        user.admin === true
    );
}

export function userIsDocReviewer(user) {
    if (!user) return false;
    return (
        user.role === "Director of Nursing" ||
        user.role === "Administrator / RN" ||
        user.role === "Clinical Coordinator / Manager" ||
        user.role === "IT / Operations" ||
        user.admin === true
    );
}

export function userCanSignClinical(user) {
    if (!user) return false;
    return [
        "Director of Nursing",
        "Administrator / RN",
        "CNA",
        "HHA",
        "SN",
        "Clinical Coordinator / Manager",
    ].includes(user.role);
    // NOTE: admin flag does NOT grant signing — must be a clinical role
}
